import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError, resolveImg, variantWidth } from "@/lib/api";
import { useAuth, isManager } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { List, SquareKanban, Trash2, Banknote, UsersRound, CircleDot, Building2, HardHat, CalendarClock, ArrowLeft, CaseSensitive, Hash, Clock } from "lucide-react";
import { useGroupSort, GroupHeader } from "@/components/shared/GroupSort";
import { toast } from "sonner";
import { useIsMobile, useIsNarrow } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useListNav } from "@/components/shared/ListNav";
import { useListFilters } from "@/hooks/useListFilters";
import { ProjectEditDialog } from "@/components/ProjectEditDialog";
import { ProjectTeamCoverageDialog } from "@/components/ProjectTeamCoverageDialog";
import { storage } from "@/lib/storage";
import { ProjectBoard } from "@/components/projects/ProjectBoard";
import { useOpenPhasesGuard } from "@/components/projects/OpenPhasesGuard";

const STATUSES = ["Poptávka", "Nabídka", "Smlouva podepsána", "V realizaci", "Dokončeno", "Fakturováno", "Záruka"];

// Nová zakázka se zadává přes ProjectEditDialog (mode="create") — jeden
// design s úpravou zakázky, včetně geokódování a náhledu mapy.

// 345: jednotná lišta — rychlé záložky, Filtr, Řadit (pole + směr), pohledy vpravo.
const DONE = ["Dokončeno", "Fakturováno", "Záruka"];
const PREP = ["Poptávka", "Nabídka", "Smlouva podepsána"];
const TABS = [
  { value: "vse", label: "Vše", test: null },
  { value: "bezi", label: "Běží", test: (p) => p.status === "V realizaci" },
  { value: "pripravujeme", label: "Připravujeme", test: (p) => PREP.includes(p.status) },
  { value: "hotove", label: "Hotové", test: (p) => DONE.includes(p.status) },
];
const today = () => new Date().toISOString().slice(0, 10);
const isLate = (p) => !!p.end_date && p.end_date.slice(0, 10) < today() && !DONE.includes(p.status);
const termOf = (p) => (!p.end_date ? "none" : isLate(p) ? "late" : DONE.includes(p.status) ? "past"
  : p.end_date.slice(0, 7) === today().slice(0, 7) ? "month" : "later");
const TERMS = [{ value: "late", label: "Po termínu" }, { value: "month", label: "Tento měsíc" }, { value: "later", label: "Později" },
  { value: "past", label: "Hotové v termínu" }, { value: "none", label: "Bez termínu" }];
// 378: Zobrazení (V) — Seskupit (Stav / Termín / Investor / Stavbyvedoucí) a Řadit (Název / Číslo / Termín / Cena / Založeno).
const PROJ_ORDERS = {
  name: { label: "Název", icon: CaseSensitive, dirs: ["A → Z", "Z → A"], def: "asc", val: (p) => p.name || "" },
  code: { label: "Číslo zakázky", icon: Hash, dirs: ["Vzestupně", "Sestupně"], def: "asc", val: (p) => p.code || "" },
  end_date: { label: "Termín dokončení", icon: CalendarClock, dirs: ["Nejdřív", "Nejpozději"], def: "asc", val: (p) => p.end_date || "" },
  contract_price: { label: "Cena díla", icon: Banknote, dirs: ["Od nejnižší", "Od nejvyšší"], def: "desc", val: (p) => p.contract_price ?? "" },
  created: { label: "Datum založení", icon: Clock, dirs: ["Nejstarší", "Nejnovější"], def: "desc", val: (p) => p.created_at || "" },
};
const PROJ_GROUPS = {
  status: { label: "Stav", icon: CircleDot, key: (p) => p.status || "", order: (a, b) => STATUSES.indexOf(a) - STATUSES.indexOf(b), dirs: ["Poptávka první", "Záruka první"] },
  term: { label: "Termín", icon: CalendarClock, key: (p) => termOf(p), name: (k) => TERMS.find((t) => t.value === k)?.label,
    order: (a, b) => TERMS.findIndex((t) => t.value === a) - TERMS.findIndex((t) => t.value === b), dirs: ["Po termínu první", "Obráceně"] },
  investor: { label: "Investor", icon: Building2, key: (p) => p.investor?.name || "", none: "Bez investora" },
  manager: { label: "Stavbyvedoucí", icon: HardHat, key: (p) => p.site_manager_name || "", none: "Bez stavbyvedoucího" },
};
const byProjName = (a, b) => (a.name || "").localeCompare(b.name || "", "cs");
const shortDate = (iso) => (iso ? `${Number(iso.slice(8, 10))}. ${Number(iso.slice(5, 7))}. ${iso.slice(0, 4)}` : "—");

// Pasportový donut (133/B): zelená = hotové třídy, amber = rozpracované,
// šedá = prázdné; střed done/total, title vypisuje třídy.
function PasportDonut({ pasport, testid }) {
  const { done = 0, partial = 0, empty = 0, total = 0, classes = [] } = pasport || {};
  const r = 18, C = 2 * Math.PI * r;
  const segs = [];
  let acc = 0;
  [[done, "hsl(var(--success))"], [partial, "#F59E0B"], [empty, "hsl(var(--border))"]].forEach(([n, color]) => {
    if (n > 0 && total > 0) { segs.push({ color, offset: acc / total, frac: n / total }); acc += n; }
  });
  const title = classes.length ? classes.map((c) => `${c.name} ${c.ordered}/${c.total}`).join(" · ") : "Bez pasportu";
  return (
    <span className="relative block w-11 h-11 shrink-0" title={title} data-testid={testid}>
      <svg viewBox="0 0 44 44" className="w-11 h-11 -rotate-90">
        {total === 0 && <circle cx="22" cy="22" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="5" />}
        {segs.map((s, i) => (
          <circle key={i} cx="22" cy="22" r={r} fill="none" stroke={s.color} strokeWidth="5"
            strokeDasharray={`${Math.max(s.frac * C - 1.5, 0.5)} ${C}`} strokeDashoffset={-s.offset * C} />
        ))}
      </svg>
      <span className={`absolute inset-0 grid place-items-center text-[11px] font-extrabold tabular-nums ${
        total === 0 ? "text-muted-foreground" : done === total ? "text-success" : ""}`}>
        {total === 0 ? "—" : `${done}/${total}`}
      </span>
    </span>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const manager = isManager(user);
  const [projects, setProjects] = useState([]);
  const phasesGuard = useOpenPhasesGuard();   // 342: ukončení zakázky s nedokončenými fázemi
  const [investors, setInvestors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [q, setQ] = useState("");
  // Výchozí je tabulka; pamatuje se jen výslovná volba. Koš se do paměti
  // nezapisuje — po obnovení poslední zakázky by uživatel přišel na prázdnou záložku.
  const overviewRole = ["OWNER", "ASSISTANT", "MANAGER"].includes(user?.role);
  const [view, setViewState] = useState(() => {
    const v = storage.get("projects_view");
    if (v === "cards" || v === "board") return "board";   // 304: Karty nahradila Tabule fází
    if (v === "prehled") return overviewRole ? "prehled" : "table";
    return "table";
  });
  const setView = (v) => { setViewState(v); if (v !== "trash") storage.set("projects_view", v); };
  const [overview, setOverview] = useState(null);   // id → řádek z /projects-overview
  const [tab, setTabState] = useState(() => (TABS.some((t) => t.value === storage.get("projects_tab")) ? storage.get("projects_tab") : "vse"));
  const setTab = (v) => { setTabState(v); storage.set("projects_tab", v); };
  const nav = useNavigate();
  const [trash, setTrash] = useState([]);   // koš — majitel a asistentka
  // 236: rychlá akce „Nová zakázka“ na úvodu (?nova=1)
  const [createOpen, setCreateOpen] = useState(() => isManager(user) && new URLSearchParams(window.location.search).get("nova") === "1");
  // 298: Vytvořit › Zakázka z levého sloupce, i když už je stránka Zakázky otevřená
  const loc = useLocation();
  useEffect(() => {
    if (isManager(user) && new URLSearchParams(loc.search).get("nova") === "1") setCreateOpen(true);
  }, [loc.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const [createStatus, setCreateStatus] = useState(null);   // 304: Přidat ve sloupci tabule = zakázka v té fázi
  const [coverageOpen, setCoverageOpen] = useState(false);   // 169/C5 — pokrytí týmu
  const canCoverage = ["OWNER", "ASSISTANT"].includes(user?.role);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  // 304: tabule — další krok a dny bez akce u každé zakázky (načte se s první návštěvou Tabule)
  const [steps, setSteps] = useState(null);
  const loadSteps = () => api.get("/projects/next-steps").then((r) => setSteps(r.data || {})).catch(() => setSteps({}));
  useEffect(() => { if (view === "board" && steps === null) loadSteps(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = (id, status) => setProjects((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
  // Přesun na tabuli: hned na místě, server zapíše historii a úkoly nové fáze (workflow); jde vrátit.
  const moveProject = async (p, stage) => {
    const from = p.status, to = stage.statuses.includes(from) ? from : stage.key;
    if (to === from) return;
    setStatus(p.id, to);
    try {
      await phasesGuard.run((x) => api.post(`/projects/${p.id}/status`, { status: to, ...x }));
      loadSteps();
      toast.success(`${p.name} → ${stage.label}`, { action: { label: "Vrátit", onClick: async () => {
        setStatus(p.id, from);
        try { await api.post(`/projects/${p.id}/status`, { status: from }); loadSteps(); }
        catch (e) { setStatus(p.id, to); toast.error(formatApiError(e.response?.data?.detail)); }
      } } });
    } catch (e) {
      setStatus(p.id, from);
      if (!e.cancelled) toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const loadTrash = () => { if (["OWNER", "ASSISTANT"].includes(user?.role)) api.get("/projects-trash").then((r) => setTrash(r.data || [])).catch(() => {}); };
  const restore = async (pid) => {
    try { await api.post(`/projects/${pid}/restore`); toast.success("Zakázka obnovena."); load(); loadTrash(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // Obnovením poslední zakázky záložka Koš zmizí — ať uživatel nezůstane na prázdnu.
  useEffect(() => { if (view === "trash" && !trash.length) setViewState("table"); }, [view, trash.length]);
  // Přehled se načítá líně při prvním otevření záložky (133/B2).
  useEffect(() => {
    if (view === "prehled" && overviewRole && overview === null) {
      api.get("/projects-overview")
        .then((r) => setOverview(Object.fromEntries((r.data.rows || []).map((x) => [x.id, x]))))
        .catch(() => setOverview({}));
    }
  }, [view, overviewRole, overview]);
  useEffect(() => {
    load(); loadTrash();
    if (isManager(user)) {
      api.get("/investors").then((r) => setInvestors(r.data)).catch(() => {});
      api.get("/team").then((r) => setManagers((r.data || []).filter((m) => ["OWNER", "MANAGER"].includes(m.role) && m.status !== "inactive"))).catch(() => {});
    }
  }, []); // eslint-disable-line

  const mobile = useIsMobile();
  const narrow = useIsNarrow();   // telefon a tablet na výšku: řádky místo tabulky (ta by přetékala)
  const groups = useMemo(() => [
    { key: "status", label: "Stav", icon: CircleDot, options: STATUSES.map((x) => ({ value: x, label: x })), match: (p, v) => p.status === v },
    ...(manager ? [
      { key: "investor", label: "Investor", icon: Building2, options: investors.map((i) => ({ value: i.id, label: i.name })), match: (p, v) => p.investor_id === v },
      { key: "manager", label: "Stavbyvedoucí", icon: HardHat, options: managers.map((m) => ({ value: m.id, label: m.name })), match: (p, v) => p.site_manager_id === v },
    ] : []),
    { key: "termin", label: "Termín", icon: CalendarClock, options: TERMS, match: (p, v) => termOf(p) === v },
  ], [investors, managers, manager]);
  const F = useListFilters(projects, {
    groups, searchTerm: q, entity: { one: "zakázka", few: "zakázky", many: "zakázek" },
    search: (p) => `${p.name || ""} ${p.code || ""} ${p.address || ""} ${p.investor?.name || ""}`,
  });
  const tabs = TABS.map((t) => ({ value: t.value, label: t.label, count: t.test ? F.visible.filter(t.test).length : F.visible.length }));
  const hasPrice = projects.some((p) => p.contract_price != null);

  const tabRows = useMemo(() => {
    const t = TABS.find((x) => x.value === tab);
    return t?.test ? F.visible.filter(t.test) : F.visible;
  }, [F.visible, tab]);
  const orders = useMemo(() => (hasPrice ? PROJ_ORDERS : Object.fromEntries(Object.entries(PROJ_ORDERS).filter(([k]) => k !== "contract_price"))), [hasPrice]);
  const GS = useGroupSort(tabRows, { storageKey: "projects_order", legacyKey: "projects_sort", groups: PROJ_GROUPS, orders,
    def: { g: "none", gd: 0, k: "name", dir: "asc" }, tie: byProjName });
  const visible = GS.sorted;          // tabule a přehled: seřazené bez skupin
  const listRows = GS.shown;          // seznam: bez sbalených skupin (klávesnice jde jen po viditelných)
  const listIdx = useMemo(() => new Map(listRows.map((p, i) => [p.id, i])), [listRows]);
  const withGroups = (row) => (GS.groups
    ? GS.groups.map((g) => [<GroupHeader key={`g-${g.key}`} g={g} closed={GS.closed.has(g.key)} onToggle={GS.toggle} mobile={narrow} />,
      ...(GS.closed.has(g.key) ? [] : g.rows.map((p) => row(p, listIdx.get(p.id))))])
    : listRows.map((p, i) => row(p, i)));
  const open = (p) => nav(`/zakazky/${p.id}`, { state: { from: `/zakazky${window.location.search}` } });
  const kb = useListNav("projects", view === "table" ? listRows.length : 0, (i) => listRows[i] && open(listRows[i]), { enabled: !mobile && view === "table" });
  const late = projects.filter(isLate).length;
  const running = projects.filter((p) => p.status === "V realizaci").length;
  const views = [
    { value: "table", label: "Seznam", icon: List },
    { value: "board", label: "Tabule", icon: SquareKanban },
    ...(overviewRole ? [{ value: "prehled", label: "Přehled peněz", short: "Peníze", icon: Banknote }] : []),   // 133/B1: jen vedení
  ];

  return (
    <div className="space-y-5" data-testid="projects-page">
      <ListHeader title="Zakázky" mobile={mobile}
        summary={<>{projects.length} {F.plural(projects.length)} · {running} běží{late ? <> · <span className="text-destructive font-semibold">{late} po termínu</span></> : null}</>}
        search={q} onSearch={setQ} searchPh="Hledat zakázku, místo…"
        primary={manager ? { label: "Zakázka", full: "Nová zakázka", onClick: () => setCreateOpen(true), testid: "new-project-btn" } : null}
        iconAction={manager && canCoverage ? { icon: UsersRound, label: "Pokrytí týmu", onClick: () => setCoverageOpen(true), testid: "team-coverage-btn" } : null}
        more={trash.length ? [{ icon: Trash2, label: `Koš (${trash.length})`, onClick: () => setView("trash"), testid: "view-tab-trash" }] : []} />

      {/* Jeden dialog pro novou zakázku i úpravu — stejný design, GPS i mapa. */}
      <ProjectEditDialog mode="create" open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setCreateStatus(null); }} createStatus={createStatus}
        team={managers} investors={investors} onCreated={() => { load(); if (steps !== null) loadSteps(); }} />

      {/* Pokrytí týmu (169/C5) — kdo chybí na kterých zakázkách + hromadné doplnění. */}
      {canCoverage && <ProjectTeamCoverageDialog open={coverageOpen} onOpenChange={setCoverageOpen} onChanged={() => load()} />}
      {phasesGuard.dialog}

      {view !== "trash" ? (
        <ListToolbar mobile={mobile} testid="project-filters"
          tabs={tabs} tab={tab} onTab={setTab} filter={F}
          sort={GS.sortProp}
          views={views} view={view} onView={setView} search={q} onSearch={setQ} searchPh="Hledat zakázku, číslo, místo…" />
      ) : (
        <button type="button" onClick={() => setView("table")} data-testid="trash-back" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} /> Zpět na zakázky
        </button>
      )}

      {view === "board" && (
        <ProjectBoard projects={visible} steps={steps} canEdit={manager} mobile={mobile}
          onOpen={open}
          onMove={moveProject}
          onAdd={(stage) => { setCreateStatus(stage.key); setCreateOpen(true); }} />
      )}

      {view === "table" && (narrow ? (
        <div className="bg-card border border-border rounded-[20px] divide-y divide-border overflow-hidden" data-testid="projects-list">
          {withGroups((p, i) => (
            <button key={p.id} type="button" onClick={() => open(p)} data-testid={`project-row-${p.id}`} {...kb.rowProps(i)}
              className={`w-full text-left flex items-center gap-3 px-4 py-3 active:bg-secondary/60 outline-none ${kb.sel === i ? "bg-brand/[0.05] shadow-[inset_2px_0_0_hsl(var(--accent-orange))]" : ""}`}>
              <span className="min-w-0 flex-1">
                <span className="block font-bold text-[15.5px] truncate">{p.name}</span>
                <span className="block text-[13px] text-muted-foreground truncate">{[p.code, p.address].filter(Boolean).join(" · ") || "—"}</span>
              </span>
              <span className="shrink-0 flex flex-col items-end gap-1">
                <StatusBadge status={p.status} />
                {p.end_date && <span className={`text-xs tabular-nums ${isLate(p) ? "text-destructive font-semibold" : "text-muted-foreground"}`}>{shortDate(p.end_date)}</span>}
              </span>
            </button>
          ))}
          {visible.length === 0 && <p className="text-muted-foreground p-4">Žádná zakázka neodpovídá filtru.</p>}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[20px] overflow-hidden overflow-x-auto">
          {/* min-w nutné: mřížka míchá pevné stopy s fr — bez ní se sloupce smrsknou.
              Peníze má nově Přehled — tabulka je pro všechny role stejná (133/C). */}
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[96px_2fr_1.4fr_110px_160px] items-center gap-3 px-4 bg-secondary/60 border-b border-border">
              {["Číslo", "Název", "Místo", "Termín", "Stav"].map((h, i, arr) => (
                <span key={h} className={`min-h-[46px] flex items-center text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground ${i >= arr.length - 2 ? "justify-end" : ""}`}>{h}</span>
              ))}
            </div>
            {withGroups((p, i) => (
              <button key={p.id} onClick={() => open(p)} data-testid={`project-row-${p.id}`} {...kb.rowProps(i)}
                className={`w-full text-left grid grid-cols-[96px_2fr_1.4fr_110px_160px] items-center gap-3 px-4 py-3 border-b border-border hover:bg-secondary/50 transition-colors outline-none ${kb.sel === i ? "bg-brand/[0.05] shadow-[inset_2px_0_0_hsl(var(--accent-orange))]" : ""}`}>
                <span className="text-sm text-muted-foreground tabular-nums">{p.code || "—"}</span>
                <span className="flex items-center gap-3 min-w-0">
                  <span className="w-11 h-11 shrink-0 rounded-[10px] bg-secondary bg-cover bg-center"
                    style={{ backgroundImage: `url('${resolveImg(p.cover_photo, variantWidth(44)) || ""}')` }} />
                  <span className="font-bold text-[16px] truncate">{p.name}</span>
                </span>
                <span className="text-sm text-muted-foreground truncate">{p.address || "—"}</span>
                <span className={`text-sm tabular-nums text-right ${isLate(p) ? "text-destructive font-semibold" : "text-muted-foreground"}`}>{shortDate(p.end_date)}</span>
                <span className="justify-self-end"><StatusBadge status={p.status} /></span>
              </button>
            ))}
            {visible.length === 0 && <p className="text-muted-foreground p-4">Žádná zakázka neodpovídá filtru.</p>}
          </div>
        </div>
      ))}

      {/* Přehled (varianta C): pasport donut + peníze na řádek — jen vedení. */}
      {view === "prehled" && overviewRole && (
        <div className="bg-card border border-border rounded-[20px] divide-y divide-border" data-testid="projects-overview-list">
          {visible.map((p) => {
            const ov = overview?.[p.id];
            const invoiced = ov?.invoiced_total || 0;
            const collected = ov?.collected_total || 0;
            // 166/A1: skutečný zbytek k inkasu (gross vs. gross), ne vyfakturováno bez DPH − inkaso s DPH.
            const restInv = ov?.outstanding_gross ?? (invoiced - collected);
            const contract = ov?.contract_price || 0;
            const costs = (ov?.material_total || 0) + (ov?.labor_total || 0);
            return (
              <button key={p.id} onClick={() => open(p)} data-testid={`overview-row-${p.id}`}
                className="w-full text-left flex items-start md:items-center gap-3.5 px-4 py-3.5 hover:bg-secondary/50 transition-colors">
                <PasportDonut pasport={ov?.pasport} testid={`overview-donut-${p.id}`} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="font-bold truncate">{p.name}</span>
                    {p.address && <span className="text-sm text-muted-foreground truncate">· {p.address}</span>}
                    <span className="md:hidden ml-auto"><StatusBadge status={p.status} /></span>
                  </span>
                  <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-semibold tabular-nums mt-1.5">
                    {ov ? (
                      <>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#5588CC" }} /> <span title="Materiál, subdodávky, služby, sklad a pokladna — vše kromě mezd.">Nákupy</span> {formatCZK(ov.material_total)}</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#B4823C" }} /> Práce {formatCZK(ov.labor_total)}</span>
                        {invoiced > 0 ? (
                          <>
                            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full shrink-0 bg-success" /> Inkasováno {formatCZK(collected)}</span>
                            {restInv > 0.5 && <span className="text-amber-600 dark:text-amber-400">zbývá inkasovat {formatCZK(restInv)}</span>}
                          </>
                        ) : (
                          <span className="text-muted-foreground font-normal">nic nevyfakturováno</span>
                        )}
                      </>
                    ) : <span className="text-muted-foreground font-normal">Načítám…</span>}
                  </span>
                  {ov && contract > 0 && (
                    <span className="relative block h-1.5 rounded-full bg-secondary mt-2 overflow-hidden max-w-[440px]" data-testid={`overview-cashline-${p.id}`}>
                      <span className="absolute inset-y-0 left-0 bg-success rounded-full" style={{ width: `${Math.min((collected / contract) * 100, 100)}%` }} />
                      <span className="absolute inset-y-0 w-[2px] bg-foreground/70" style={{ left: `${Math.min((costs / contract) * 100, 100)}%` }} />
                    </span>
                  )}
                </span>
                <span className="hidden md:block shrink-0"><StatusBadge status={p.status} /></span>
              </button>
            );
          })}
          {visible.length === 0 && <p className="text-muted-foreground p-4">Žádná zakázka neodpovídá filtru.</p>}
        </div>
      )}

      {/* Koš — vlastní pohled, ne panel pod seznamem. Záložka se ukazuje jen když je co obnovovat. */}
      {view === "trash" && (
        <div className="bg-card border border-border rounded-[20px] p-4 md:p-5" data-testid="projects-trash">
          <p className="text-[13px] text-muted-foreground mb-3">Smazané zakázky se definitivně odstraní po 7 dnech. Do té doby je jde obnovit i s daty — docházkou, úkoly i doklady.</p>
          <div className="space-y-2">
            {trash.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 flex-wrap rounded-[12px] border border-border px-3.5 py-2.5" data-testid={`trash-row-${t.id}`}>
                <span className="min-w-0">
                  <span className="block text-sm font-bold truncate">{t.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    Smazal {t.deletedByName || "—"} · {t.daysLeft === 0 ? "smaže se dnes" : `smaže se za ${t.daysLeft} ${t.daysLeft === 1 ? "den" : t.daysLeft < 5 ? "dny" : "dní"}`}
                  </span>
                </span>
                <Button variant="outline" onClick={() => restore(t.id)} className="h-9 font-bold shrink-0" data-testid={`trash-restore-${t.id}`}>Obnovit</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
